// EmailSettingsPage - Paramètres Email (IMAP, SMTP) connecté au backend
import { useState, useEffect } from "react";
import { Mail, Inbox, Send, RefreshCw, Save, Plus, TestTube, Check, X, Trash2, Loader2, Edit2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmailSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  
  // Boîtes entrantes (IMAP)
  const [boxes, setBoxes] = useState([]);
  // Boîtes sortantes (SMTP)
  const [senders, setSenders] = useState([]);
  // Stats
  const [stats, setStats] = useState(null);
  
  // Dialog pour ajouter/éditer
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(null); // 'box' ou 'sender'
  const [editingItem, setEditingItem] = useState(null);
  
  // Formulaire boîte entrante
  const [boxForm, setBoxForm] = useState({
    name: "",
    email: "",
    imap: { host: "imap.gmail.com", port: 993, username: "", password: "", use_ssl: true, folder: "INBOX" }
  });
  
  // Formulaire boîte sortante
  const [senderForm, setSenderForm] = useState({
    name: "",
    email: "",
    display_name: "OmniHub IA",
    smtp: { host: "smtp.gmail.com", port: 587, username: "", password: "", use_tls: true }
  });

  // Charger les données
  const loadData = async () => {
    setLoading(true);
    try {
      const [boxesRes, sendersRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/mail/boxes`),
        axios.get(`${API}/api/mail/senders`),
        axios.get(`${API}/api/mail/stats`)
      ]);
      setBoxes(boxesRes.data.boxes || []);
      setSenders(sendersRes.data.senders || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Erreur chargement:", err);
      toast.error("Erreur de chargement des paramètres email");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Tester connexion IMAP
  const testBox = async (boxId) => {
    setTesting(`box-${boxId}`);
    try {
      const res = await axios.post(`${API}/api/mail/boxes/${boxId}/test`);
      if (res.data.success) {
        toast.success(`Connexion IMAP réussie - ${res.data.message_count} emails`);
      } else {
        toast.error(`Échec: ${res.data.error}`);
      }
    } catch (err) {
      toast.error("Erreur de test");
    } finally {
      setTesting(null);
    }
  };

  // Tester connexion SMTP
  const testSender = async (senderId) => {
    setTesting(`sender-${senderId}`);
    try {
      const res = await axios.post(`${API}/api/mail/senders/${senderId}/test`);
      if (res.data.success) {
        toast.success("Connexion SMTP réussie");
      } else {
        toast.error(`Échec: ${res.data.error}`);
      }
    } catch (err) {
      toast.error("Erreur de test");
    } finally {
      setTesting(null);
    }
  };

  // Ouvrir dialog pour ajouter
  const openAddDialog = (type) => {
    setDialogType(type);
    setEditingItem(null);
    if (type === 'box') {
      setBoxForm({
        name: "",
        email: "",
        imap: { host: "imap.gmail.com", port: 993, username: "", password: "", use_ssl: true, folder: "INBOX" }
      });
    } else {
      setSenderForm({
        name: "",
        email: "",
        display_name: "OmniHub IA",
        smtp: { host: "smtp.gmail.com", port: 587, username: "", password: "", use_tls: true }
      });
    }
    setDialogOpen(true);
  };

  // Ouvrir dialog pour éditer
  const openEditDialog = (type, item) => {
    setDialogType(type);
    setEditingItem(item);
    if (type === 'box') {
      setBoxForm({
        name: item.name || "",
        email: item.email || "",
        imap: item.imap || { host: "", port: 993, username: "", password: "", use_ssl: true, folder: "INBOX" }
      });
    } else {
      setSenderForm({
        name: item.name || "",
        email: item.email || "",
        display_name: item.display_name || "",
        smtp: item.smtp || { host: "", port: 587, username: "", password: "", use_tls: true }
      });
    }
    setDialogOpen(true);
  };

  // Sauvegarder
  const handleSave = async () => {
    setSaving(true);
    try {
      if (dialogType === 'box') {
        if (editingItem) {
          await axios.put(`${API}/api/mail/boxes/${editingItem.id}`, boxForm);
          toast.success("Boîte entrante mise à jour");
        } else {
          await axios.post(`${API}/api/mail/boxes`, boxForm);
          toast.success("Boîte entrante créée");
        }
      } else {
        if (editingItem) {
          await axios.put(`${API}/api/mail/senders/${editingItem.id}`, senderForm);
          toast.success("Boîte sortante mise à jour");
        } else {
          await axios.post(`${API}/api/mail/senders`, senderForm);
          toast.success("Boîte sortante créée");
        }
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  // Supprimer
  const handleDelete = async (type, id) => {
    if (!confirm("Supprimer cette configuration ?")) return;
    try {
      if (type === 'box') {
        await axios.delete(`${API}/api/mail/boxes/${id}`);
        toast.success("Boîte entrante supprimée");
      } else {
        await axios.delete(`${API}/api/mail/senders/${id}`);
        toast.success("Boîte sortante supprimée");
      }
      loadData();
    } catch (err) {
      toast.error("Erreur de suppression");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Paramètres Email</h1>
            <p className="text-sm text-zinc-400">Configuration des serveurs IMAP/SMTP</p>
          </div>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white">{stats.boxes}</p>
                  <p className="text-sm text-zinc-400">Boîtes entrantes</p>
                </div>
                <Inbox className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white">{stats.senders}</p>
                  <p className="text-sm text-zinc-400">Boîtes sortantes</p>
                </div>
                <Send className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white">{stats.rules}</p>
                  <p className="text-sm text-zinc-400">Règles actives</p>
                </div>
                <Mail className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white">{stats.executions?.total || 0}</p>
                  <p className="text-sm text-zinc-400">Traitements</p>
                </div>
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="imap" className="space-y-6">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="imap">
            <Inbox className="w-4 h-4 mr-2" />
            IMAP (Réception)
          </TabsTrigger>
          <TabsTrigger value="smtp">
            <Send className="w-4 h-4 mr-2" />
            SMTP (Envoi)
          </TabsTrigger>
        </TabsList>

        {/* IMAP - Boîtes entrantes */}
        <TabsContent value="imap" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openAddDialog('box')} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter boîte entrante
            </Button>
          </div>

          {boxes.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-12 text-center">
                <Inbox className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">Aucune boîte entrante configurée</p>
                <Button onClick={() => openAddDialog('box')} className="mt-4">
                  Configurer une boîte IMAP
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {boxes.map(box => (
                <Card key={box.id} className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                          <Inbox className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <CardTitle className="text-base text-white">{box.name}</CardTitle>
                          <CardDescription>{box.email}</CardDescription>
                        </div>
                        <Badge className={box.enabled ? "bg-green-500/20 text-green-400" : "bg-zinc-700 text-zinc-400"}>
                          {box.enabled ? "Actif" : "Inactif"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => testBox(box.id)}
                          disabled={testing === `box-${box.id}`}
                        >
                          {testing === `box-${box.id}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <TestTube className="w-4 h-4" />
                          )}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEditDialog('box', box)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-400 hover:text-red-300" onClick={() => handleDelete('box', box.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-zinc-500">Serveur:</span>
                        <span className="ml-2 text-white">{box.imap?.host}:{box.imap?.port}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Dossier:</span>
                        <span className="ml-2 text-white">{box.imap?.folder || "INBOX"}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">SSL:</span>
                        <span className="ml-2 text-white">{box.imap?.use_ssl ? "Oui" : "Non"}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Dernière vérif:</span>
                        <span className="ml-2 text-white">{box.last_check ? new Date(box.last_check).toLocaleString('fr-FR') : "Jamais"}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SMTP - Boîtes sortantes */}
        <TabsContent value="smtp" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openAddDialog('sender')} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter boîte sortante
            </Button>
          </div>

          {senders.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-12 text-center">
                <Send className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">Aucune boîte sortante configurée</p>
                <Button onClick={() => openAddDialog('sender')} className="mt-4">
                  Configurer une boîte SMTP
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {senders.map(sender => (
                <Card key={sender.id} className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                          <Send className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <CardTitle className="text-base text-white">{sender.name}</CardTitle>
                          <CardDescription>{sender.email}</CardDescription>
                        </div>
                        {sender.is_default && (
                          <Badge className="bg-amber-500/20 text-amber-400">Par défaut</Badge>
                        )}
                        <Badge className={sender.enabled ? "bg-green-500/20 text-green-400" : "bg-zinc-700 text-zinc-400"}>
                          {sender.enabled ? "Actif" : "Inactif"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => testSender(sender.id)}
                          disabled={testing === `sender-${sender.id}`}
                        >
                          {testing === `sender-${sender.id}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <TestTube className="w-4 h-4" />
                          )}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEditDialog('sender', sender)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-400 hover:text-red-300" onClick={() => handleDelete('sender', sender.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-zinc-500">Serveur:</span>
                        <span className="ml-2 text-white">{sender.smtp?.host}:{sender.smtp?.port}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Nom affiché:</span>
                        <span className="ml-2 text-white">{sender.display_name}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">TLS:</span>
                        <span className="ml-2 text-white">{sender.smtp?.use_tls ? "Oui" : "Non"}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog Ajout/Edition */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingItem ? "Modifier" : "Ajouter"} {dialogType === 'box' ? "boîte entrante (IMAP)" : "boîte sortante (SMTP)"}
            </DialogTitle>
          </DialogHeader>
          
          {dialogType === 'box' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input
                    value={boxForm.name}
                    onChange={(e) => setBoxForm({...boxForm, name: e.target.value})}
                    placeholder="Support, Contact..."
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={boxForm.email}
                    onChange={(e) => setBoxForm({...boxForm, email: e.target.value})}
                    placeholder="contact@example.com"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Serveur IMAP</Label>
                  <Input
                    value={boxForm.imap.host}
                    onChange={(e) => setBoxForm({...boxForm, imap: {...boxForm.imap, host: e.target.value}})}
                    placeholder="imap.gmail.com"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input
                    type="number"
                    value={boxForm.imap.port}
                    onChange={(e) => setBoxForm({...boxForm, imap: {...boxForm.imap, port: parseInt(e.target.value)}})}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Utilisateur</Label>
                  <Input
                    value={boxForm.imap.username}
                    onChange={(e) => setBoxForm({...boxForm, imap: {...boxForm.imap, username: e.target.value}})}
                    placeholder="user@example.com"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mot de passe</Label>
                  <Input
                    type="password"
                    value={boxForm.imap.password}
                    onChange={(e) => setBoxForm({...boxForm, imap: {...boxForm.imap, password: e.target.value}})}
                    placeholder="••••••••"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <span className="text-sm text-white">SSL/TLS</span>
                <Switch
                  checked={boxForm.imap.use_ssl}
                  onCheckedChange={(v) => setBoxForm({...boxForm, imap: {...boxForm.imap, use_ssl: v}})}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input
                    value={senderForm.name}
                    onChange={(e) => setSenderForm({...senderForm, name: e.target.value})}
                    placeholder="Support, Marketing..."
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={senderForm.email}
                    onChange={(e) => setSenderForm({...senderForm, email: e.target.value})}
                    placeholder="noreply@example.com"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Nom affiché</Label>
                <Input
                  value={senderForm.display_name}
                  onChange={(e) => setSenderForm({...senderForm, display_name: e.target.value})}
                  placeholder="OmniHub IA"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Serveur SMTP</Label>
                  <Input
                    value={senderForm.smtp.host}
                    onChange={(e) => setSenderForm({...senderForm, smtp: {...senderForm.smtp, host: e.target.value}})}
                    placeholder="smtp.gmail.com"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input
                    type="number"
                    value={senderForm.smtp.port}
                    onChange={(e) => setSenderForm({...senderForm, smtp: {...senderForm.smtp, port: parseInt(e.target.value)}})}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Utilisateur</Label>
                  <Input
                    value={senderForm.smtp.username}
                    onChange={(e) => setSenderForm({...senderForm, smtp: {...senderForm.smtp, username: e.target.value}})}
                    placeholder="user@example.com"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mot de passe</Label>
                  <Input
                    type="password"
                    value={senderForm.smtp.password}
                    onChange={(e) => setSenderForm({...senderForm, smtp: {...senderForm.smtp, password: e.target.value}})}
                    placeholder="••••••••"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <span className="text-sm text-white">STARTTLS</span>
                <Switch
                  checked={senderForm.smtp.use_tls}
                  onCheckedChange={(v) => setSenderForm({...senderForm, smtp: {...senderForm.smtp, use_tls: v}})}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
